<!-- components/Survey/questions/MultiSelect.svelte -->
<script>
  import { createEventDispatcher } from 'svelte';
  import { scale } from 'svelte/transition';
  
  // Add props that Question.svelte is passing
  export let title = ''; // Add this even though we don't use it
  export let description = null; // Add this even though we don't use it
  export let options = [];
  export let minSelect = 0;
  export let currentValue = [];
  
  const dispatch = createEventDispatcher();
  
  // Track if items are ready to animate (only after first render)
  let isInitialRender = true;
  
  function toggleOption(optionId) {
    // Create a new array to avoid mutations
    let newValue;
    
    if (currentValue.includes(optionId)) {
      // Remove from selections
      newValue = currentValue.filter(id => id !== optionId);
    } else {
      // Add to selections
      newValue = [...currentValue, optionId];
    }
    
    // Send the updated value to parent
    console.log(`Dispatching new value after toggle: ${JSON.stringify(newValue)}`);
    dispatch('select', newValue);
  }
  
  function handleContinue() {
    if (minSelect > 0 && currentValue.length < minSelect) {
      return; // Don't continue if minimum not met
    }
    
    // Dispatch a continue event to let the parent component know to advance
    console.log("Continue clicked, dispatching advance event");
    dispatch('advance');
  }
  
  // After first render, mark initial render as complete
  import { onMount } from 'svelte';
  onMount(() => {
    // Use a timeout to ensure DOM is fully rendered
    setTimeout(() => {
      isInitialRender = false;
    }, 50);
  });
</script>

<div class="multi-select">
  <div class="options-container">
    {#each options as option, i}
      <div 
        class="option"
        class:selected={currentValue.includes(option.id)}
        on:click={() => toggleOption(option.id)}
      >
        <div class="option-content">
          <div class="checkbox">
            {#if currentValue.includes(option.id)}
              <div class="check" class:no-animation={isInitialRender}>
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
                </svg>
              </div>
            {/if}
          </div>
          <div class="label">{option.label}</div>
        </div>
      </div>
    {/each}
  </div>
  
  <div class="action-container">
    <button 
      class="continue-button" 
      on:click={handleContinue}
      disabled={minSelect > 0 && currentValue.length < minSelect}
    >
      Continue
    </button>
    
    {#if minSelect > 0}
      <div class="selection-count">
        Selected: {currentValue.length} / {minSelect}+
      </div>
    {/if}
  </div>
</div>

<style>
  .multi-select {
    width: 100%;
  }
  
  .options-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
  
  .option {
    padding: 1rem;
    border-radius: 8px;
    background-color: white;
    border: 2px solid #e0e0e0;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  .option:hover {
    border-color: #BB945C; /* Cavs gold color */
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .option.selected {
    border-color: #6A0032; /* Cavs wine color */
    background-color: rgba(134, 0, 56, 0.05);
  }
  
  .option-content {
    display: flex;
    align-items: center;
  }
  
  .checkbox {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 2px solid #ccc;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }
  
  .option.selected .checkbox {
    border-color: #6A0032;
    background-color: #6A0032;
  }
  
  .check {
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .check:not(.no-animation) {
    animation: fade-in 0.2s ease;
  }
  
  @keyframes fade-in {
    from { opacity: 0; transform: scale(0.5); }
    to { opacity: 1; transform: scale(1); }
  }
  
  .check svg {
    fill: white;
  }
  
  .label {
    font-size: 1.1rem;
    color: #333;
    font-weight: 500;
  }
  
  .action-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .continue-button {
    background-color: #6A0032; /* Cavs wine color */
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
  }
  
  .continue-button:hover:not(:disabled) {
    background-color: #6d0030;
  }
  
  .continue-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  
  .selection-count {
    font-size: 0.9rem;
    color: #666;
  }
  
  @media (max-width: 768px) {
    .option {
      padding: 0.75rem;
    }
    
    .label {
      font-size: 1rem;
    }
  }
</style>